
import { BlogPostFormData } from '@/types/blog';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Eye, Loader2, PenLine, Save, Trash2, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface BlogHeaderProps {
  isNew: boolean;
  formData: BlogPostFormData;
  isSubmitting: boolean;
  isLoaded: boolean;
  activeTab: string;
  hasUnsavedChanges: boolean;
  lastSaved: Date | null;
  isSaving: boolean;
  onTabChange: (tab: string) => void;
  onDelete: () => void;
  onSave: () => void;
  promptIfUnsaved: (path: string) => void;
}

export const BlogHeader = ({
  isNew,
  formData,
  isSubmitting,
  isLoaded,
  activeTab,
  hasUnsavedChanges,
  lastSaved,
  isSaving,
  onTabChange,
  onDelete,
  onSave,
  promptIfUnsaved
}: BlogHeaderProps) => {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            if (hasUnsavedChanges) {
              promptIfUnsaved('/dashboard/blog/posts');
            } else {
              navigate('/dashboard/blog/posts');
            }
          }}
        >
          <ArrowLeft size={18} />
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">
          {isNew ? "Create New Post" : (formData.title ? `Edit: ${formData.title}` : "Edit Post")}
        </h1>
      </div>
      <div className="flex items-center gap-2">
        {lastSaved && (
          <div className="text-xs text-slate-500 flex items-center mr-2">
            <Clock size={12} className="mr-1" />
            {isSaving ? "Saving..." : `Last saved: ${lastSaved.toLocaleTimeString()}`}
          </div>
        )}
        {!isNew && (
          <Button
            variant="outline"
            size="sm"
            className="text-red-500 hover:text-red-600"
            onClick={onDelete}
            disabled={isSubmitting}
          >
            <Trash2 size={16} className="mr-2" />
            Delete
          </Button>
        )}
        
        <Button
          variant="outline"
          size="sm"
          disabled={isSubmitting || !isLoaded || activeTab === "preview"}
          onClick={() => onTabChange("preview")}
        >
          <Eye size={16} className="mr-2" />
          Preview
        </Button>
        
        <Button
          size="sm"
          disabled={isSubmitting || !isLoaded}
          onClick={onSave}
        >
          {isSubmitting ? (
            <>
              <Loader2 size={16} className="mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save size={16} className="mr-2" />
              Save
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
